import Link from "next/link";
import {
  isWithinEntryWindow,
  GTA5_PRIZE_COUNT,
  GTA5_CLOSE_LABEL_TZ,
} from "@/constants/gta5-giveaway";
import {
  PROMO_BANNER,
  PROMO_WINDOW_GLOBAL,
  isPromoBannerActive,
} from "@/constants/promo-banner";
import { useTrans } from "@/utils/useTrans";
import { useHalloween2026 } from "@/hooks/useHalloween2026";
import { Halloween2026Strip } from "@/v2-components/event/halloween-2026-strip";
import { usePromotionTracking } from "@/libs/analytics/use-promotion-tracking";

/**
 * 전역 상단 파란 띠 프로모 배너. `PageTemplate` 의 sticky 안(GNB 아래) + 템플릿·페이지에
 * 직접 마운트. **실측 8곳 / 7파일** (2026-09-09 `git grep -c "<PromoBanner" -- src`).
 *
 * 노출·문구·목적지는 전부 `constants/promo-banner.ts` 한 곳에서 온다 — 마운트가 여러
 * 곳이라 두 벌이 되면 화면마다 다른 마감일이 나간다.
 *
 * ## ★현 캠페인 = GTA V meets TactSuit Giveaway (#5655)
 *
 * 종전 AI 프로모(`enabled: false`)에서 갈아탔다. 바뀐 것:
 *
 * ```
 *   시작 게이트   **새로 생겼다** — 종전엔 마감만 있어 캠페인 전부터 뜰 수 있었다
 *   문구          `t()` 를 안 쓴다 (아래)
 *   목적지        응모 폼 앵커 — 그냥 랜딩으로 보내면 히어로·3스텝을 다시 스크롤한다
 *   할인 슬롯     제거 — 기브어웨이라 할인이 없다
 * ```
 *
 * ## ★시간 소스가 **서버 시계**다 (#5702 — 이 PR 에서 올렸다)
 *
 * 종전엔 이 배너만 클라이언트 `Date.now()` 로 판정해서 팝업·랜딩과 갈렸다:
 *
 * ```
 *   ① 시계가 틀어진 기기   시작 전에 띠가 보인다 → 눌러 가면 404(서버 판정)
 *   ② 경계 초에 걸친 로드   SSR 은 서버 시각, 리렌더는 브라우저 시각 → hydration 불일치
 * ```
 *
 * 지금은 `_document` 가 서버 시계로 창 여부를 판정해 전역에 세우고 여기서 읽는다.
 * 어느 길이 왜 막혀 있었는지(96개 GSSP · `_app` ASO)는 그 파일 docblock 에 있다.
 *
 * ⚠️ **남은 대가 하나** — `_document` 는 클라 전환 때 안 돌므로, 세션 중 경계를 넘으면
 * **새로고침해야** 반영된다. 종전엔 전환만 해도 떴으므로 이 한 경우는 후퇴다.
 * 광고 배너라 받아들였다.
 *
 * ## ★문구는 여전히 시각에 의존하면 안 된다
 *
 * 서버 판정이어도 **표시 시점의 정밀도**는 그대로다 — `_document` 가 요청 시각에
 * 한 번 재고 그 값이 세션 동안 고정된다. 카운트다운·「마감 임박」류가 들어오면
 * 초 단위로 틀리는 것이 화면에 보인다. 가드가 `PROMO_BANNER.copy` 에 시:분·
 * 카운트다운 표기가 없음을 잠근다(정적 타임존 라벨 `(PT)` 는 허용).
 */
export default function PromoBanner() {
  /**
   * ★**창 여부는 서버가 정한다** (#5702).
   *
   * ```
   *   SSR    서버 시계로 직접 잰다
   *   클라   _document 가 세워 둔 전역을 읽는다 (같은 요청의 서버 시계)
   * ```
   *
   * 두 값이 같은 요청의 서버 시계에서 나오므로 **hydration 이 어긋나지 않는다.**
   *
   * ⚠️ **「같은 요청」은 GSSP 페이지에서만 성립한다.** 정적 프리렌더 페이지는 값이
   * 빌드 시각에 굳으므로 아예 띠를 안 그린다(`hidePromoBanner` · #5655 R3 x1).
   * 종전엔 클라가 `Date.now()` 로 다시 계산해서, 시계를 바꾼 기기에서 리렌더 때
   * 뒤집혔다(띠는 떴는데 눌러 가면 404).
   *
   * ⚠️ **`=== true` 로 좁게 읽는다.** 스크립트가 어떤 이유로든 안 실행되면
   * `undefined` 이고, 그건 「닫힘」으로 떨어져야 한다 — 광고를 못 보는 쪽이 없는
   * 캠페인을 광고하는 쪽보다 낫다.
   */
  const withinWindow =
    typeof window === "undefined"
      ? isWithinEntryWindow(new Date())
      : (window as unknown as Record<string, unknown>)[PROMO_WINDOW_GLOBAL] ===
        true;

  /**
   * ★**문구는 `gnb` 에서 온다** (#5739 다국어).
   *
   * ⚠️ `event` 가 아니다 — 이 배너는 `PageTemplate` 에 있어 **사이트 전역**
   * (소비 페이지 61개)인데 `event` 를 싣는 페이지는 **2곳**(홈·랜딩)뿐이다.
   * `event` 로 두면 나머지 59페이지가 키 이름을 그대로 그린다.
   *
   * `gnb` 는 띠배너가 뜨는 모든 페이지가 싣는다(실측: `gnb` 없는 페이지엔
   * `PageTemplate` 도 없다).
   */
  const { t } = useTrans(["gnb"]);
  const message = t("promoGta5Message");
  const detail = t("promoGta5Detail", { prizeCount: GTA5_PRIZE_COUNT });
  const ends = t("promoGta5Ends", { closeLabelTz: GTA5_CLOSE_LABEL_TZ });
  const cta = t("promoGta5Cta");

  /**
   * ★**할로윈 2026 기간에는 이 자리를 할로윈 띠로 바꾼다** (#6061 H5).
   *
   * 같은 슬롯(GNB 아래 · 마운트 8곳)을 쓰므로 둘이 겹치지 않게 여기서 고른다. 할로윈이 우선이다 — 기간이 정해진
   * 이벤트라서다(당시 비-prod 는 GTA 띠가 항상 켜져 있었다 — #6149 이후로는 둘 다 기간으로만 판정한다).
   * 정적 페이지 제외(`hidePromoBanner`)는 그대로 따른다 — 이 컴포넌트 자체가 거기서 안 그려진다.
   */
  const halloween = useHalloween2026();
  const active = !halloween && isPromoBannerActive({ withinWindow });
  // 노출·클릭 측정(D1). 훅이라 아래 early return 들보다 먼저 부른다 — 안 그리는 렌더는 `null` 로 끈다.
  const { attach: attachPromo, onClickCapture: onPromoClick } =
    usePromotionTracking(
      active ? { id: PROMO_BANNER.promotionId, slot: "top_strip" } : null,
    );
  if (halloween) return <Halloween2026Strip />;

  /**
   * ⚠️ **게이트는 훅 **뒤**에 온다** — 위 `useTrans` 를 이 `return null` 아래 두면
   * 「띠가 보이다 사라지는」 렌더에서 훅 개수가 달라져 React 가 던진다
   * (`Application error: a client-side exception has occurred`). 선행 결함이었고
   * eslint `react-hooks/rules-of-hooks` 가 이미 error 로 짚고 있었다.
   */
  if (!active) return null;

  return (
    <Link
      href={PROMO_BANNER.href}
      ref={attachPromo}
      onClickCapture={onPromoClick}
      className="block bg-blue-60 text-white"
      aria-label={`${message} · ${detail} · ${ends}`}
    >
      {/* Desktop: 중앙 정렬 한 줄 + 구분선 */}
      <div className="hidden h-10 items-center justify-center gap-4 px-4 text-xs leading-4 tracking-[0.4px] laptop:flex">
        <span className="font-600">{message}</span>
        <span className="h-3 w-px bg-white/40" aria-hidden />
        <span>{detail}</span>
        <span className="h-3 w-px bg-white/40" aria-hidden />
        <span>{ends}</span>
        <span className="h-3 w-px bg-white/40" aria-hidden />
        <span className="underline underline-offset-2">{cta}</span>
      </div>

      {/* Mobile: 볼드 메시지 + 요약 한 줄 */}
      <div className="flex h-11 items-center justify-center gap-2 px-4 text-xs leading-4 tracking-[0.4px] laptop:hidden">
        <span className="font-600">{detail}</span>
        <span>{ends}</span>
      </div>
    </Link>
  );
}
